// frontend/src/components/ConnectionsCard.js


import React, { useEffect, useMemo, useState } from "react";
import { api } from "api";
import ConnectionsModal from "./ConnectionsModal";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";


const toAvatar = (user) =>
  user?.profile_pic_url ||
  user?.avatar_url ||
  user?.photo_url ||
  user?.image_url ||
  DEFAULT_PROFILE_IMAGE;


function pickUser(row) {
  const user =
    row?.user ||
    row?.member ||
    row?.following ||
    row?.follower ||
    row?.addressee ||
    row?.receiver ||
    row?.requester ||
    row?.sender ||
    row;


  const name =
    user?.display_name ||
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    (user?.id ? `User #${user.id}` : "User");


  return {
    id: user?.id,
    name,
    username: user?.username,
    profile_pic_url:
      user?.profile_pic_url ||
      user?.avatar_url ||
      user?.photo_url ||
      user?.image_url ||
      null,
    avatar_url: user?.avatar_url || null,
  };
}


function normalizeList(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.users)
        ? data.users
        : [];


  return list.map(pickUser).filter((user) => user?.id != null);
}


const ConnectionsCard = ({
  meId,
  ownerName,
  viewerId,
  initialTab = "overview",
  hideTitle = false,
  frameless = false,
}) => {
  const [open, setOpen] = useState(false);
  const [modalTab, setModalTab] = useState(initialTab);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);


  const headers = useMemo(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null;


    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);


  const fetchFollowing = async () => {
    if (!meId) {
      setFollowing([]);
      return;
    }


    try {
      const { data } = await api.get(
        `/connections/following/${meId}`,
        { headers }
      );


      setFollowing(normalizeList(data));
    } catch {
      setFollowing([]);
    }
  };


  const fetchFollowers = async () => {
    if (!meId) {
      setFollowers([]);
      return;
    }


    try {
      const { data } = await api.get(
        `/connections/followers/${meId}`,
        { headers }
      );


      setFollowers(normalizeList(data));
    } catch {
      setFollowers([]);
    }
  };


  useEffect(() => {
    fetchFollowing();
    fetchFollowers();


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId]);


  const handleOpen = (tab) => {
    setModalTab(tab);
    setOpen(true);


    window.dispatchEvent(
      new CustomEvent("connections:open")
    );
  };


  const handleClose = () => {
    setOpen(false);


    window.dispatchEvent(
      new CustomEvent("connections:close")
    );
  };


  const outerClass = frameless
    ? ""
    : "rounded-2xl bg-white px-5 py-4";


  const sectionTitleClass =
    "mb-2 ml-2 text-sm text-gray-700";


  const isSelf =
    viewerId != null &&
    meId != null &&
    String(viewerId) === String(meId);


  const title = isSelf
    ? "Your Connections"
    : "Connections";


  const StackAvatars = ({
    users,
    max = 5,
  }) => {
    const shown = users.slice(0, max);
    const remaining = users.length - shown.length;


    return (
      <div className="ml-1 flex justify-start -space-x-3">
        {shown.map((user) => (
          <img
            key={user.id}
            src={toAvatar(user)}
            alt={user.name || `User #${user.id}`}
            className="
              h-10 w-10
              rounded-full object-cover
              ring-2 ring-white
            "
            draggable={false}
          />
        ))}


        {remaining > 0 && (
          <div
            className="
              grid h-10 w-10 place-items-center
              rounded-full bg-gray-100
              text-xs text-gray-600
              ring-2 ring-white
            "
          >
            +{remaining}
          </div>
        )}
      </div>
    );
  };


  return (
    <>
      <section className={outerClass}>
        {!hideTitle && (
          <div className="mb-3 flex items-center">
            <h4 className="text-[16px] font-black tracking-[-0.02em] text-blue-600">
              {title}
            </h4>
          </div>
        )}


        {following.length === 0 && followers.length === 0 ? (
          <div className="text-sm text-gray-400">
            No connections yet
          </div>
        ) : (
          <>
            {following.length > 0 && (
              <section className={hideTitle || frameless ? "" : ""}>
                <button
                  type="button"
                  onClick={() => handleOpen("following")}
                  className="
                    group block w-full
                    !min-h-0 !min-w-0
                    bg-transparent p-0
                    text-left
                  "
                >
                  <StackAvatars users={following} />


                  <div className="ml-2 mt-2 text-sm text-gray-800 group-hover:font-bold">
                    <span className="font-semibold">{following.length}</span> following
                  </div>
                </button>
              </section>
            )}


            {followers.length > 0 && (
              <section className={following.length > 0 ? "mt-3" : ""}>
                <button
                  type="button"
                  onClick={() => handleOpen("followers")}
                  className="
                    group block w-full
                    !min-h-0 !min-w-0
                    bg-transparent p-0
                    text-left
                  "
                >
                  <StackAvatars users={followers} />


                  <div className="ml-2 mt-2 text-sm text-gray-800 group-hover:font-bold">
                    <span className="font-semibold">{followers.length}</span>{" "}
                    follower{followers.length === 1 ? "" : "s"}
                  </div>
                </button>
              </section>
            )}
          </>
        )}
      </section>


      {open && (
        <ConnectionsModal
          open
          onClose={handleClose}
          meId={meId}
          viewerId={viewerId}
          ownerName={ownerName}
          initialTab={modalTab}
        />
      )}
    </>
  );
};


export default ConnectionsCard;
